/// <reference types="./Auth.d.ts" />
import { notOk, ok, } from '@dossierhq/core';
import { ensureRequired } from './utils/ValidationUtils.js';
export async function authCreateSession(databaseAdapter, context, provider, identifier, readonly) {
    const assertion = ensureRequired({ provider, identifier });
    if (assertion.isError())
        return assertion;
    return await databaseAdapter.authCreateSession(context, provider, identifier, readonly, null);
}
export async function authCreatePrincipalSyncEvent(databaseAdapter, context, event) {
    return await databaseAdapter.authCreateSession(context, event.provider, event.identifier, false, event);
}
/** N.B. don't use this function normally, instead use {@link authCreateSession}.
 *
 * This function is used to create a session for a sync event where we only have the subject id,
 * not provider and identifier which we normally use.
 */
export async function authCreateSyncSessionForSubject(databaseAdapter, context, arg) {
    if (!arg.subjectId) {
        return notOk.BadRequest('No subject provided');
    }
    return await databaseAdapter.authCreateSyncSessionForSubject(context, arg);
}
//TODO harmonize with core
function verifyAuthKeyFormat(authKey) {
    if (authKey.trimStart() !== authKey) {
        return notOk.BadRequest(`Invalid authKey (${authKey}), can’t start with whitespace`);
    }
    if (authKey.trimEnd() !== authKey) {
        return notOk.BadRequest(`Invalid authKey (${authKey}), can’t end with whitespace`);
    }
    if (authKey.includes(',')) {
        // Don't allow commas since it makes CSV error prone in HTTP header
        return notOk.BadRequest(`Invalid authKey (${authKey}), can’t contain comma`);
    }
    return ok(undefined);
}
export function verifyAuthKeysFormat(authKeys) {
    for (const authKey of authKeys) {
        const result = verifyAuthKeyFormat(authKey);
        if (result.isError())
            return result;
    }
    return ok(undefined);
}
export async function authResolveAuthorizationKey(authorizationAdapter, context, authKey) {
    if (authKey === '') {
        return ok({ authKey: '', resolvedAuthKey: '' });
    }
    const verifyResult = verifyAuthKeyFormat(authKey);
    if (verifyResult.isError())
        return verifyResult;
    const resolvedResult = await authorizationAdapter.resolveAuthorizationKeys(context, [authKey]);
    if (resolvedResult.isError())
        return resolvedResult;
    const resolvedAuthKey = resolvedResult.value.find((it) => it.authKey === authKey);
    if (!resolvedAuthKey) {
        return notOk.Generic(`Authorization adapter didn't return a key when resolving authKey (${authKey})`);
    }
    return ok(resolvedAuthKey);
}
export async function authResolveAuthorizationKeys(authorizationAdapter, context, requestedAuthKeys) {
    let authKeys = requestedAuthKeys && requestedAuthKeys.length > 0 ? requestedAuthKeys : context.defaultAuthKeys;
    if (authKeys.length === 0) {
        return notOk.BadRequest('No authKeys provided');
    }
    const defaultIndex = authKeys.indexOf('');
    if (defaultIndex !== -1) {
        if (authKeys.length === 1) {
            return ok([{ authKey: '', resolvedAuthKey: '' }]);
        }
        authKeys = authKeys.filter((it) => it !== '');
    }
    const verifyResult = verifyAuthKeysFormat(authKeys);
    if (verifyResult.isError())
        return verifyResult;
    const resoledAuthKeysResult = await authorizationAdapter.resolveAuthorizationKeys(context, authKeys);
    if (resoledAuthKeysResult.isError())
        return resoledAuthKeysResult;
    const resoledAuthKeys = resoledAuthKeysResult.value;
    if (defaultIndex !== -1) {
        resoledAuthKeys.push({ authKey: '', resolvedAuthKey: '' });
    }
    return ok(resoledAuthKeys);
}
export async function authVerifyAuthorizationKey(authorizationAdapter, context, actualAuthKey) {
    const resolveResult = await authResolveAuthorizationKeys(authorizationAdapter, context, [
        actualAuthKey.authKey,
    ]);
    if (resolveResult.isError())
        return resolveResult;
    for (const expectedAuthKey of resolveResult.value) {
        if (expectedAuthKey.resolvedAuthKey === actualAuthKey.resolvedAuthKey) {
            return ok(undefined);
        }
    }
    return notOk.NotAuthorized('Wrong authKey provided');
}
//# sourceMappingURL=Auth.js.map